import { APP } from 'src/app/config/app';
import { FilterOptionByIdLoader, FilterOptionLoader, IFilterOption, IFilterOptionPage } from 'src/app/models';

export class FilterOptionList {
  options: IFilterOption[] = [];
  selected: IFilterOption[] = [];
  disabled = false;
  loading = false;
  loadingMore = false;

  private page = 1;
  private search?: string;
  private lastPage = false;
  private latestRequest = 0;
  private valuesOf?: IFilterOption[];
  private valuesCache: any[] = [];

  constructor(
    private loader: FilterOptionLoader,
    private toOption: (item: any) => IFilterOption,
  ) {}

  reload() {
    this.page = 1;
    this.lastPage = false;
    return this.load();
  }

  async loadMore() {
    if (this.lastPage || this.loading || this.loadingMore) return;
    this.page += 1;
    await this.load(true);
  }

  searchFor(search: string) {
    this.page = 1;
    this.lastPage = false;
    this.search = search || undefined;
    return this.load();
  }

  get values(): any[] {
    if (this.valuesOf !== this.selected) {
      this.valuesOf = this.selected;
      this.valuesCache = this.selected.map((option) => option.value);
    }
    return this.valuesCache;
  }

  selectValues(values: any[]) {
    const byId = new Map(this.options.map((option) => [option.id, option]));
    this.selected = (values ?? [])
      .filter((value) => value != null)
      .map((value) => byId.get(value.id) ?? this.toOption(value));
    this.merge([], true);
  }

  async resolveSelectedLabels(loadById: FilterOptionByIdLoader) {
    const missingIds = this.selected.filter((option) => !option.label).map((option) => Number(option.id));
    if (!missingIds.length) return;
    const results = await Promise.allSettled(missingIds.map((id) => loadById(id)));
    const resolvedById = new Map<any, IFilterOption>();
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled' || !result.value) return;
      const option = this.toOption({ ...(result.value as object), id: missingIds[index] });
      resolvedById.set(option.id, option);
    });
    const withResolvedLabel = (option: IFilterOption) => resolvedById.get(option.id) ?? option;
    this.selected = this.selected.map(withResolvedLabel);
    this.options = this.options.map(withResolvedLabel);
  }

  selectIds(ids: (number | string)[]) {
    this.selectValues(this.options.filter((option) => ids.includes(option.id)).map((option) => option.value));
  }

  clear() {
    this.options = [];
    this.selected = [];
    this.page = 1;
    this.search = undefined;
    this.lastPage = false;
  }

  private async load(append = false) {
    const request = ++this.latestRequest;
    this.loading = !append;
    this.loadingMore = append;
    let result: IFilterOptionPage;
    try {
      result = await this.loader(this.page, this.search);
    } catch {
      if (request === this.latestRequest && !append) this.options = [];
      this.lastPage = true;
      return;
    } finally {
      if (request === this.latestRequest) {
        this.loading = false;
        this.loadingMore = false;
      }
    }
    // a slower earlier request must not overwrite the options of a newer one
    if (request !== this.latestRequest) return;
    this.lastPage = !!result?.last;
    this.merge((result?.content ?? []).map(this.toOption), append);
  }

  // a selected option missing from the loaded page would otherwise disappear from the list
  private merge(loaded: IFilterOption[], append: boolean) {
    const options = append ? [...this.options, ...loaded] : loaded;
    const byId = new Map<any, IFilterOption>(options.map((option) => [option.id, option]));
    const orphans = this.selected.filter((option) => !byId.has(option.id));
    this.selected = this.selected.map((option) => byId.get(option.id) ?? option);
    this.options = [...orphans, ...byId.values()];
  }

  static pageSize() {
    return APP.DEFAULT_PAGE_SIZE;
  }
}
